import { useEffect, useMemo, useState } from "react"
import { Button, Card, Space } from "antd"
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import { useCrud } from "@/hooks/useCrud"
import { ID } from "@/types/common"
import { Toast } from "@/components/Toast"
import fileAssetApi from "../file-asset/api"
import { FileAsset } from "../file-asset/types"
import qrCodeApi from "./api"
import QrCodeModal from "./components/QrCodeModal"
import QrCodeTable from "./components/QrCodeTable"
import { QrCode, QrCodeFormData } from "./types"

const QrCodeManagement = () => {
  const [fileOptions, setFileOptions] = useState<FileAsset[]>([])

  const api = useMemo(
    () => ({
      getList: (params?: { page?: number; pageSize?: number }) =>
        qrCodeApi.getList(params),
      create: qrCodeApi.create,
      update: (id: ID, data: QrCodeFormData | Partial<QrCodeFormData>) =>
        qrCodeApi.update(id, data as QrCodeFormData),
      delete: qrCodeApi.delete,
    }),
    [],
  )

  const {
    items,
    loading,
    opLoading,
    isModalOpen,
    editingItem,
    selectedIds,
    setSelectedIds,
    pagination,
    handleAdd,
    handleEdit,
    handleSave,
    handleDelete,
    handleBatchDelete,
    handleCloseModal,
    handlePageChange,
  } = useCrud<QrCode, QrCodeFormData>({
    api,
    entityName: "二维码",
    pagination: {
      enabled: true,
      pageSize: 10,
    },
  })

  useEffect(() => {
    const loadFileOptions = async () => {
      try {
        const pageSize = 100
        const firstPage = await fileAssetApi.getList({ page: 1, pageSize })
        const restPageNumbers = Array.from(
          { length: Math.max(firstPage.totalPages - 1, 0) },
          (_, index) => index + 2,
        )
        const restPages = await Promise.all(
          restPageNumbers.map((page) => fileAssetApi.getList({ page, pageSize })),
        )

        setFileOptions([
          ...firstPage.data,
          ...restPages.flatMap((page) => page.data),
        ])
      } catch (error: any) {
        Toast.error(error.message || "加载文件选项失败")
      }
    }

    loadFileOptions()
  }, [])

  return (
    <div className="p-6">
      <Card>
        <div className="mb-4 flex items-center justify-between">
          <span className="text-gray-500">共 {pagination.total} 个二维码</span>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              loading={opLoading}
            >
              新增二维码
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleBatchDelete}
              disabled={selectedIds.length === 0}
              loading={opLoading}
            >
              批量删除 {selectedIds.length > 0 && `(${selectedIds.length})`}
            </Button>
          </Space>
        </div>

        <QrCodeTable
          items={items}
          loading={loading}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onEdit={handleEdit}
          onDelete={handleDelete}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
          }}
          onPageChange={handlePageChange}
        />
      </Card>

      <QrCodeModal
        isOpen={isModalOpen}
        qrCode={editingItem}
        loading={opLoading}
        fileOptions={fileOptions}
        onClose={handleCloseModal}
        onSave={handleSave}
      />
    </div>
  )
}

export default QrCodeManagement
